import React, { useState, useEffect } from 'react';
import '../styles/UserSettings.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faCheckCircle, faExclamationTriangle, faUser, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import ApiService from '../utils/ApiService';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const UserSettings: React.FC = () => {
  const [userData, setUserData] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  useEffect(() => {
    // Get user data from local storage
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
    // Fetch creation date from Firebase Auth if available
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserData((prev: any) => ({
          ...prev,
          metadata: user.metadata,
          email: user.email,
          displayName: user.displayName,
        }));
      }
    });
    return () => unsubscribe();
  }, []);

  // Open dialog for password change
  // const openChangePasswordDialog = () => {
  //   // Reset form state
  //   setCurrentPassword('');
  //   setNewPassword('');
  //   setConfirmPassword('');
  //   setSuccessMessage('');
  //   setErrorMessage('');
  //   // Open dialog
  //   setIsDialogOpen(true);
  // };

  // Close dialog
  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  // Form validation
  const isFormValid = () => {
    if (!currentPassword) {
      setErrorMessage('Current password is required');
      return false;
    }
    
    if (newPassword !== confirmPassword) {
      setErrorMessage('New passwords do not match');
      return false;
    }
    
    if (newPassword.length < 8) {
      setErrorMessage('New password must be at least 8 characters long');
      return false;
    }
    
    return true;
  };

  // Handle password update
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear messages
    setSuccessMessage('');
    setErrorMessage('');
    
    // Validate form
    if (!isFormValid()) {
      return;
    }
    
    try {
      setIsUpdating(true);
      
      // Call API to update password (no token argument)
      await ApiService.updateUser({ 
        password: newPassword,
        current_password: currentPassword
      });
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Show success message
      setSuccessMessage('Password updated successfully');
      
      // Close dialog after a short delay
      setTimeout(() => {
        if (successMessage) {
          closeDialog();
        }
      }, 2000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setErrorMessage(`Failed to update password: ${errorMessage}`);
    } finally {
      setIsUpdating(false);
    }
  };
  
  return (
    <div className="user-settings-container">
      <div className="user-settings-header">
        <h1>User Settings</h1>
        <p>Manage your account settings and preferences</p>
      </div>
      
      <div className="settings-content">
        {/* Account Information Section */}
        <div className="settings-section">
          <h2>Account Information</h2>
          {userData && (
            <div className="account-info">
              <div className="info-row">
                <label><FontAwesomeIcon icon={faUser} /> Full Name</label>
                <div>{userData.displayName || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email || 'User'}</div>
              </div>
              <div className="info-row">
                <label><FontAwesomeIcon icon={faEnvelope} /> Email</label>
                <div>{userData.email}</div>
              </div>
              <div className="info-row">
                <label><FontAwesomeIcon icon={faKey} /> Account Created</label>
                <div>{
                  userData && userData.metadata && userData.metadata.creationTime
                    ? new Date(userData.metadata.creationTime).toLocaleDateString()
                    : userData && userData.createdAt
                      ? new Date(userData.createdAt).toLocaleDateString()
                      : 'Unknown'
                }</div>
              </div>
              {/* Organization row removed as it is not relevant anymore */}
              {/* <div className="info-row">
                <label><FontAwesomeIcon icon={faBuilding} /> Organization</label>
                <div>{userData.organization || 'Not specified'}</div>
              </div> */}
            </div>
          )}
        </div>
        {/* Security section hidden for now */}
        {/* <div className="settings-section">
          <h2>Security</h2>
          <p className="section-description">
            Update your password and manage account security settings.
          </p>
          {userData && (
            // ...existing code for change password button...
          )}
        </div> */}
      </div>
      
      {/* Password Change Dialog */}
      {isDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog password-dialog">
            <h2>Change Password</h2>
            <p>Enter your current password and a new password below.</p>
            
            <form onSubmit={handleUpdatePassword} className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">
                  <FontAwesomeIcon icon={faKey} /> Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">
                  <FontAwesomeIcon icon={faKey} /> New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">
                  <FontAwesomeIcon icon={faKey} /> Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                />
              </div>
              
              {successMessage && (
                <div className="message success">
                  <FontAwesomeIcon icon={faCheckCircle} /> {successMessage}
                </div>
              )}
              
              {errorMessage && (
                <div className="message error">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
                </div>
              )}
              
              <div className="dialog-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={closeDialog}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="update-button"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSettings;